import React from "react";
import canopen from "../stack/canopen";

// Minimal CANopen SDO client UI (expedited only for now)
export default function SdoTool() {
  const [nodeId, setNodeId] = React.useState(1);
  const [indexHex, setIndexHex] = React.useState("0x1000");
  const [subHex, setSubHex] = React.useState("0x00");
  
  // Write panel state
  const [writeIndexHex, setWriteIndexHex] = React.useState("0x2000");
  const [writeSubHex, setWriteSubHex] = React.useState("0x00");
  const [writeHex, setWriteHex] = React.useState("");
  const [writeDecimal, setWriteDecimal] = React.useState("");
  const [writeText, setWriteText] = React.useState("");
  const [writeFloat, setWriteFloat] = React.useState("");
  const [writeFormat, setWriteFormat] = React.useState("hex");
  
  // SDO sweep state
  const [sweepStartHex, setSweepStartHex] = React.useState("0x1000");
  const [sweepEndHex, setSweepEndHex] = React.useState("0x2FFF");
  const [sweepOnlySub0, setSweepOnlySub0] = React.useState(true);
  const [sweepRunning, setSweepRunning] = React.useState(false);
  const [sweepResults, setSweepResults] = React.useState([]);
  const [sweepProgress, setSweepProgress] = React.useState(0);
  const [sweepTotal, setSweepTotal] = React.useState(0);
  
  // Use ref for immediate sweep control
  const sweepRunningRef = React.useRef(false);
  
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState("");
  const [error, setError] = React.useState("");

  const parseHex16 = (s) => {
    const t = (s || "").trim().toLowerCase().replace(/^0x/, "");
    if (!/^([0-9a-f]{1,4})$/.test(t)) throw new Error("Index must be hex (<= 0xFFFF)");
    return parseInt(t, 16);
  };
  const parseHex8 = (s) => {
    const t = (s || "").trim().toLowerCase().replace(/^0x/, "");
    if (!/^([0-9a-f]{1,2})$/.test(t)) throw new Error("Sub must be hex (<= 0xFF)");
    return parseInt(t, 16);
  };
  const parseHexBytes = (s) => {
    const t = (s || "").trim().replace(/0x/gi, "").replace(/[^0-9a-fA-F]/g, "");
    if (t.length === 0) return new Uint8Array();
    if (t.length % 2 !== 0) throw new Error("Byte string must have even hex length");
    const out = new Uint8Array(t.length / 2);
    for (let i = 0; i < t.length; i += 2) out[i / 2] = parseInt(t.slice(i, i + 2), 16);
    return out;
  };
  
  // Convert various input formats to bytes for writing
  const parseWriteData = (format, value) => {
    switch (format) {
      case "hex":
        return parseHexBytes(value);
      case "decimal": {
        const num = parseInt(value || "0", 10);
        if (!Number.isFinite(num)) throw new Error("Invalid decimal number");
        // Auto-detect size based on value range
        if (num >= 0 && num <= 255) return new Uint8Array([num]);
        if (num >= -128 && num <= 127) return new Uint8Array([num & 0xFF]);
        if (num >= 0 && num <= 65535) return new Uint8Array([num & 0xFF, (num >> 8) & 0xFF]);
        if (num >= -32768 && num <= 32767) return new Uint8Array([num & 0xFF, (num >> 8) & 0xFF]);
        // 32-bit
        return new Uint8Array([
          num & 0xFF, 
          (num >> 8) & 0xFF, 
          (num >> 16) & 0xFF, 
          (num >> 24) & 0xFF
        ]);
      }
      case "text": {
        if (typeof TextEncoder !== "undefined") {
          return new Uint8Array(new TextEncoder().encode(value || ""));
        } else {
          // Fallback for older browsers
          const bytes = [];
          for (let i = 0; i < (value || "").length; i++) {
            const code = value.charCodeAt(i);
            if (code < 128) bytes.push(code);
            else bytes.push(63); // '?' for non-ASCII
          }
          return new Uint8Array(bytes);
        }
      }
      case "float": {
        const num = parseFloat(value || "0");
        if (!Number.isFinite(num)) throw new Error("Invalid float number");
        const buffer = new ArrayBuffer(4);
        new DataView(buffer).setFloat32(0, num, true); // little-endian
        return new Uint8Array(buffer);
      }
      default:
        throw new Error("Unknown format");
    }
  };
  
  const toHex = (bytes) => Array.from(bytes).map((b) => b.toString(16).padStart(2, "0").toUpperCase()).join(" ");

  const stripTrailingNulls = (s) => {
    let i = s.length;
    while (i > 0 && s.charCodeAt(i - 1) === 0) i--;
    return s.slice(0, i);
  };

  // (waitForFrameOnce removed; replaced by waitForSdo which handles aborts)

  // decodeAbort helper no longer needed here

  // Removed raw listener helpers; using central SDO client now

  const handleRead = async (override) => {
    setError("");
    setResult("");
    try {
      const nid = override?.nodeId ?? nodeId;
      const idxHex = override?.indexHex ?? indexHex;
      const subHexStr = override?.subHex ?? subHex;
      if (!Number.isFinite(nid) || nid < 1 || nid > 127) throw new Error("Node ID 1..127");
      const index = parseHex16(idxHex);
      const sub = parseHex8(subHexStr);
      setBusy(true);
      const bytes = await canopen.readSDO(nid, index, sub);
      const txt = (typeof TextDecoder !== "undefined") ? stripTrailingNulls(new TextDecoder().decode(bytes)) : "";
      // TODO: Node name setting will be implemented later
      // if (index === 0x1008 && sub === 0x00 && txt) canopen.setNodeName(nid, txt);
      const maybeStr = txt ? ` | "${txt}"` : "";
      setResult(`Read OK (${bytes.length}): ${toHex(bytes)}${maybeStr}`);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleWrite = async () => {
    setError("");
    setResult("");
    try {
      if (!Number.isFinite(nodeId) || nodeId < 1 || nodeId > 127) throw new Error("Node ID 1..127");
      const index = parseHex16(writeIndexHex);
      const sub = parseHex8(writeSubHex);
      
      // Get the current input value based on selected format
      const currentValue = writeFormat === "hex" ? writeHex :
                          writeFormat === "decimal" ? writeDecimal :
                          writeFormat === "text" ? writeText :
                          writeFloat;
      
      const data = parseWriteData(writeFormat, currentValue);
      if (data.length === 0) throw new Error("Provide data to write");
      
      setBusy(true);
      await canopen.writeSDO(nodeId, index, sub, data);
      setResult(`Write OK (${data.length} bytes, ${writeFormat}): ${toHex(data)}`);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setBusy(false);
    }
  };

  const handleSweep = async () => {
    setError("");
    setResult("");
    setSweepResults([]);
    
    try {
      if (!Number.isFinite(nodeId) || nodeId < 1 || nodeId > 127) throw new Error("Node ID 1..127");
      const startIndex = parseHex16(sweepStartHex);
      const endIndex = parseHex16(sweepEndHex);
      if (startIndex > endIndex) throw new Error("Start index must be <= end index");
      
      setSweepRunning(true);
      sweepRunningRef.current = true;
      setBusy(true);
      
      console.log(`Starting SDO sweep from 0x${startIndex.toString(16)} to 0x${endIndex.toString(16)}, sub-indices: ${sweepOnlySub0 ? '[0]' : '[0-255]'}`);
      
      const results = [];
      const subIndices = sweepOnlySub0 ? [0] : Array.from({length: 256}, (_, i) => i);
      const totalChecks = (endIndex - startIndex + 1) * subIndices.length;
      setSweepTotal(totalChecks);
      setSweepProgress(0);
      
      let checked = 0;
      
      for (let index = startIndex; index <= endIndex && sweepRunningRef.current; index++) {
        for (const sub of subIndices) {
          if (!sweepRunningRef.current) break;
          
          try {
            // Increase timeout to 250ms for more reliable detection
            const bytes = await canopen.readSDO(nodeId, index, sub, { timeoutMs: 250 });
            const txt = (typeof TextDecoder !== "undefined") ? new TextDecoder().decode(bytes).replace(/\0/g, '') : "";
            results.push({
              index: `0x${index.toString(16).toUpperCase()}`,
              sub: `0x${sub.toString(16).toUpperCase().padStart(2, '0')}`,
              size: bytes.length,
              hex: toHex(bytes),
              text: txt.length > 0 ? txt : null
            });
          } catch (e) {
            // Ignore failures - we expect most reads to fail
          }
          
          checked++;
          setSweepProgress(checked);
          
          // Yield to UI every 5 checks to keep responsive and show progress
          if (checked % 5 === 0) {
            await new Promise(resolve => setTimeout(resolve, 10));
          }
        }
      }
      
      console.log(`Sweep completed. Checked ${checked} entries, found ${results.length} readable entries`);
      setSweepResults(results);
      setResult(`Sweep completed: Found ${results.length} readable entries out of ${totalChecks} checked`);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSweepRunning(false);
      sweepRunningRef.current = false;
      setBusy(false);
    }
  };

  const stopSweep = () => {
    setSweepRunning(false);
    sweepRunningRef.current = false;
  };

  return (
    <section style={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Global Node ID */}
      <div style={{ padding: 12, borderBottom: "1px solid #e5e7eb", backgroundColor: "#f9fafb" }}>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: "bold" }}>
          Node ID
          <input type="number" min={1} max={127} value={nodeId} onChange={(e) => setNodeId(parseInt(e.target.value || "0", 10))} style={{ width: 80 }} />
        </label>
        {busy && <span style={{ opacity: 0.7, fontSize: "0.9em", marginLeft: 10 }}>Working…</span>}
      </div>
      
      {/* Read Panel */}
      <div style={{ padding: 12, borderBottom: "1px solid #e5e7eb" }}>
        <h3 style={{ margin: "0 0 12px 0", fontSize: "1.1em", fontWeight: "bold" }}>Read SDO</h3>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            Index
            <input value={indexHex} onChange={(e) => setIndexHex(e.target.value)} placeholder="0x1000" style={{ width: 120 }} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            Sub
            <input value={subHex} onChange={(e) => setSubHex(e.target.value)} placeholder="0x00" style={{ width: 80 }} />
          </label>
          <button onClick={handleRead} disabled={busy} style={{ padding: "6px 12px" }}>Read</button>
          <button
            onClick={async () => {
              await handleRead({ nodeId, indexHex: "0x1008", subHex: "0x00" });
            }}
            disabled={busy}
            title="SDO 0x1008:0"
            style={{ padding: "6px 12px", backgroundColor: "#3b82f6", color: "white", border: "none", borderRadius: "4px" }}
          >Read Device Name</button>
        </div>
      </div>
      
      {/* Write Panel */}
      <div style={{ padding: 12, borderBottom: "1px solid #e5e7eb" }}>
        <h3 style={{ margin: "0 0 12px 0", fontSize: "1.1em", fontWeight: "bold" }}>Write SDO</h3>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            Index
            <input value={writeIndexHex} onChange={(e) => setWriteIndexHex(e.target.value)} placeholder="0x2000" style={{ width: 120 }} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            Sub
            <input value={writeSubHex} onChange={(e) => setWriteSubHex(e.target.value)} placeholder="0x00" style={{ width: 80 }} />
          </label>
        </div>
        
        {/* Format Selection */}
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: "block", marginBottom: 6, fontWeight: "500" }}>Data Format:</label>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            {["hex", "decimal", "text", "float"].map(format => (
              <label key={format} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <input
                  type="radio"
                  value={format}
                  checked={writeFormat === format}
                  onChange={(e) => setWriteFormat(e.target.value)}
                />
                {format.charAt(0).toUpperCase() + format.slice(1)}
              </label>
            ))}
          </div>
        </div>
        
        {/* Data Input based on selected format */}
        <div style={{ marginBottom: 12 }}>
          {writeFormat === "hex" && (
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Hex bytes
              <input 
                value={writeHex} 
                onChange={(e) => setWriteHex(e.target.value)} 
                placeholder="01 02 03 04 or 0x01020304" 
                style={{ width: 300 }} 
              />
            </label>
          )}
          {writeFormat === "decimal" && (
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Decimal number
              <input 
                type="number" 
                value={writeDecimal} 
                onChange={(e) => setWriteDecimal(e.target.value)} 
                placeholder="123" 
                style={{ width: 150 }} 
              />
              <span style={{ fontSize: "0.8em", opacity: 0.7 }}>Auto-detects size (1, 2, or 4 bytes)</span>
            </label>
          )}
          {writeFormat === "text" && (
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Text string
              <input 
                type="text" 
                value={writeText} 
                onChange={(e) => setWriteText(e.target.value)} 
                placeholder="Hello World" 
                style={{ width: 300 }} 
              />
            </label>
          )}
          {writeFormat === "float" && (
            <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
              Float (32-bit)
              <input 
                type="number" 
                step="any" 
                value={writeFloat} 
                onChange={(e) => setWriteFloat(e.target.value)} 
                placeholder="3.14159" 
                style={{ width: 150 }} 
              />
              <span style={{ fontSize: "0.8em", opacity: 0.7 }}>IEEE 754 single precision</span>
            </label>
          )}
        </div>
        
        <button onClick={handleWrite} disabled={busy} style={{ padding: "6px 12px", backgroundColor: "#10b981", color: "white", border: "none", borderRadius: "4px" }}>
          Write
        </button>
      </div>
      
      {/* SDO Sweep Panel */}
      <div style={{ padding: 12, borderBottom: "1px solid #e5e7eb" }}>
        <h3 style={{ margin: "0 0 12px 0", fontSize: "1.1em", fontWeight: "bold" }}>SDO Sweep</h3>
        <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            Start Index
            <input value={sweepStartHex} onChange={(e) => setSweepStartHex(e.target.value)} placeholder="0x1000" style={{ width: 120 }} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            End Index
            <input value={sweepEndHex} onChange={(e) => setSweepEndHex(e.target.value)} placeholder="0x2FFF" style={{ width: 120 }} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input 
              type="checkbox" 
              checked={sweepOnlySub0} 
              onChange={(e) => setSweepOnlySub0(e.target.checked)}
            />
            Only sub-index 0x00
          </label>
        </div>
        
        <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
          {!sweepRunning ? (
            <button 
              onClick={handleSweep} 
              disabled={busy} 
              style={{ padding: "6px 12px", backgroundColor: "#f59e0b", color: "white", border: "none", borderRadius: "4px" }}
            >
              Start Sweep
            </button>
          ) : (
            <button 
              onClick={stopSweep}
              style={{ padding: "6px 12px", backgroundColor: "#ef4444", color: "white", border: "none", borderRadius: "4px" }}
            >
              Stop Sweep
            </button>
          )}
          
          {sweepRunning && (
            <div style={{ fontSize: "0.9em", opacity: 0.8 }}>
              Progress: {sweepProgress}/{sweepTotal} ({Math.round((sweepProgress / sweepTotal) * 100)}%)
            </div>
          )}
        </div>
        
        {sweepResults.length > 0 && (
          <div style={{ maxHeight: "200px", overflowY: "auto", border: "1px solid #d1d5db", borderRadius: "4px", fontSize: "0.85em" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead style={{ backgroundColor: "#f9fafb", position: "sticky", top: 0 }}>
                <tr>
                  <th style={{ padding: "6px", textAlign: "left", borderRight: "1px solid #e5e7eb" }}>Index</th>
                  <th style={{ padding: "6px", textAlign: "left", borderRight: "1px solid #e5e7eb" }}>Sub</th>
                  <th style={{ padding: "6px", textAlign: "left", borderRight: "1px solid #e5e7eb" }}>Size</th>
                  <th style={{ padding: "6px", textAlign: "left", borderRight: "1px solid #e5e7eb" }}>Hex</th>
                  <th style={{ padding: "6px", textAlign: "left" }}>Text</th>
                </tr>
              </thead>
              <tbody>
                {sweepResults.map((result, i) => (
                  <tr key={i} style={{ borderTop: i > 0 ? "1px solid #e5e7eb" : "none" }}>
                    <td style={{ padding: "4px 6px", borderRight: "1px solid #e5e7eb", fontFamily: "monospace" }}>{result.index}</td>
                    <td style={{ padding: "4px 6px", borderRight: "1px solid #e5e7eb", fontFamily: "monospace" }}>{result.sub}</td>
                    <td style={{ padding: "4px 6px", borderRight: "1px solid #e5e7eb" }}>{result.size}</td>
                    <td style={{ padding: "4px 6px", borderRight: "1px solid #e5e7eb", fontFamily: "monospace", fontSize: "0.8em" }}>
                      {result.hex.length > 30 ? result.hex.substring(0, 30) + "..." : result.hex}
                    </td>
                    <td style={{ padding: "4px 6px", fontFamily: "monospace" }}>
                      {result.text ? (result.text.length > 20 ? result.text.substring(0, 20) + "..." : result.text) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* Results */}
      <div style={{ padding: 12 }}>
        {error && <div style={{ color: "#b91c1c", marginBottom: 8, padding: 8, backgroundColor: "#fef2f2", borderRadius: 4 }}>{error}</div>}
        {result && <div style={{ color: "#065f46", padding: 8, backgroundColor: "#ecfdf5", borderRadius: 4 }}>{result}</div>}
        {!result && !error && (
          <div style={{ opacity: 0.6, fontSize: "0.9em" }}>
            <p><strong>CANopen SDO Tool</strong></p>
            <p>• Read: Supports expedited & segmented transfers (all sizes)</p>
            <p>• Write: Multiple formats - hex bytes, decimal numbers, text strings, and floats</p>
            <p>• Sweep: Discover object dictionary entries by reading index ranges</p>
            <p>• Decimal auto-detects size: 0-255 → 1 byte, 0-65535 → 2 bytes, larger → 4 bytes</p>
          </div>
        )}
      </div>
    </section>
  );
}
